/** Badge / pastille activité INSEE (SIRENE). */
import type { ReactNode } from "react";
import { useFormatters } from "../../formats/useFormatters";
import { formatDay } from "./format";
import "./sirene.css";
import type { SireneStatus } from "./types";

const LABELS: Record<SireneStatus, string> = {
  active: "Active",
  establishment_closed: "Établissement fermé",
  ceased: "Cessée / radiée",
  unknown: "Statut inconnu",
};

export type SireneInfo = {
  status?: SireneStatus | null;
  checkedAt?: string | null;
  closedOn?: string | null;
  createdOn?: string | null;
};

type SireneBadgeProps = SireneInfo & { action?: ReactNode };

export function sireneLabel(status?: SireneStatus | null): string {
  if (!status) return "";
  return LABELS[status] || "";
}

export function sireneHint(info: SireneInfo, day = formatDay): string {
  const { status, checkedAt, closedOn, createdOn } = info;
  if (!status) return "Activité INSEE non vérifiée";
  const bits: string[] = [LABELS[status]];
  if (status === "active" && createdOn) bits.push(`depuis le ${day(createdOn)}`);
  else if (createdOn) bits.push(`créée le ${day(createdOn)}`);
  if (status !== "active" && closedOn) bits.push(`fermée le ${day(closedOn)}`);
  if (checkedAt) bits.push(`vérifié le ${day(checkedAt)}`);
  return bits.join(" · ");
}

function StatusTitle({
  day,
  status,
  createdOn,
  closedOn,
}: {
  day: (iso: string | null | undefined) => string;
  status?: SireneStatus | null;
  createdOn?: string | null;
  closedOn?: string | null;
}) {
  if (!status) return <>Non vérifiée</>;
  if (status === "active") {
    return (
      <>
        <strong>Active</strong>
        {createdOn ? ` depuis le ${day(createdOn)}` : null}
      </>
    );
  }
  if (status === "establishment_closed") {
    return (
      <>
        <strong>Établissement fermé</strong>
        {closedOn ? ` le ${day(closedOn)}` : null}
      </>
    );
  }
  if (status === "ceased") {
    return (
      <>
        <strong>Cessée / radiée</strong>
        {closedOn ? ` le ${day(closedOn)}` : null}
      </>
    );
  }
  return <strong>{LABELS[status]}</strong>;
}

export function SireneBadge({ status, checkedAt, closedOn, createdOn, action }: SireneBadgeProps) {
  const { day } = useFormatters();
  const hint = sireneHint({ status, checkedAt, closedOn, createdOn }, day);
  return (
    <div className="fx-sirene-block" title={hint}>
      <p className={`fx-sirene-status is-${status || "idle"}`}>
        <StatusTitle day={day} status={status} createdOn={createdOn} closedOn={closedOn} />
      </p>
      {checkedAt ? <p className="fx-sirene-line">vérifié le {day(checkedAt)}</p> : null}
      {action}
    </div>
  );
}

export function SirenePill(info: SireneInfo & { className?: string }) {
  const { day } = useFormatters();
  const kind = info.status || "idle";
  const hint = sireneHint(info, day);
  return (
    <span
      className={`fx-sirene-pill fx-sirene-pill--${kind}${info.className ? ` ${info.className}` : ""}`}
      title={hint}
      aria-label={hint}
    />
  );
}
